// ManagementPage: installation + AC lifecycle UI.
//
// Per v2.1 §10.3 amendment: management owns the installation-level
// operations distinct from the workbench's per-session ones.
//
// Three sections:
//   1. InstallationOverview — lists registered ACs (via ListACsOp).
//   2. RegisterPanel        — promote the active session's draft AC
//                              into the installation (RegisterACOp).
//                              Only enabled when the session is
//                              COMMITTED.
//   3. ForkPanel            — pick a registered AC + a new workspace
//                              path → ForkSessionOp creates a fresh
//                              SELECTING session seeded from the AC's
//                              catalog.
//
// All ops use the same session passed in by SessionContext (lifted to
// App.tsx in slice 6). If no session is active, the page shows a hint
// pointing back to the Workbench Session tab.

import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../api/client";
import type {
  ForkSessionResult,
  ListACsResult,
  RegisterACResult,
} from "../api/types";
import { useSession } from "../workbench/SessionContext";

export function ManagementPage() {
  const { client, session, refreshSession } = useSession();
  const [acsResult, setAcsResult] = useState<ListACsResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registerResult, setRegisterResult] = useState<RegisterACResult | null>(null);
  const [forkResult, setForkResult] = useState<ForkSessionResult | null>(null);

  const loadACs = useCallback(async () => {
    if (session === null) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.runOp(session.session_id, "ListACs", { inputs: {} });
      if (!r.success || !r.result) throw new Error(r.error ?? "ListACs failed");
      setAcsResult(r.result as unknown as ListACsResult);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  }, [client, session?.session_id]);

  useEffect(() => {
    setAcsResult(null);
    setRegisterResult(null);
    setForkResult(null);
    setError(null);
    if (session !== null) {
      loadACs();
    }
  }, [session, loadACs]);

  if (session === null) {
    return (
      <section>
        <h1>Manage</h1>
        <p style={{ color: "#888" }}>
          Start a Lattice via the <em>Build → Lattice</em> tab. Lattice
          lifecycle ops (list / register / fork) operate against that
          session's data source.
        </p>
      </section>
    );
  }

  return (
    <section>
      <h1>AC Management</h1>
      <p style={subtitleStyle}>
        Installation registry, AC lifecycle (register / fork), L2 stability
        coordination. Per v2.1 §10.3 + §2.3.
      </p>

      {error && (
        <div style={errorBlockStyle}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <InstallationOverview
        installationId={session.installation_id}
        acs={acsResult}
        loading={busy}
        onRefresh={loadACs}
      />

      <RegisterPanel
        disabled={busy}
        sessionCommitted={session.scope_phase === "committed"}
        onRegister={async (workspaceDirName, description) => {
          if (session === null) return;
          setBusy(true);
          setError(null);
          setRegisterResult(null);
          try {
            const inputs: Record<string, unknown> = {};
            if (workspaceDirName.trim() !== "") inputs.workspace_dir_name = workspaceDirName.trim();
            if (description.trim() !== "") inputs.description = description.trim();
            const r = await client.runOp(session.session_id, "RegisterAC", { inputs });
            if (!r.success || !r.result) throw new Error(r.error ?? "RegisterAC failed");
            const result = r.result as unknown as RegisterACResult;
            setRegisterResult(result);
            await loadACs();
            refreshSession();
          } catch (e) {
            setError(formatError(e));
          } finally {
            setBusy(false);
          }
        }}
        lastResult={registerResult}
      />

      <ForkPanel
        disabled={busy}
        knownACs={acsResult?.ac_names ?? []}
        onFork={async (acName, newWorkspacePath) => {
          if (session === null) return;
          setBusy(true);
          setError(null);
          setForkResult(null);
          try {
            const r = await client.runOp(session.session_id, "ForkSession", {
              inputs: {
                ac_name: acName.trim(),
                new_workspace_path: newWorkspacePath.trim(),
              },
            });
            if (!r.success || !r.result) throw new Error(r.error ?? "ForkSession failed");
            setForkResult(r.result as unknown as ForkSessionResult);
            refreshSession();
          } catch (e) {
            setError(formatError(e));
          } finally {
            setBusy(false);
          }
        }}
        lastResult={forkResult}
      />
    </section>
  );
}

// ── InstallationOverview ──────────────────────────────────────────────

function InstallationOverview({
  installationId,
  acs,
  loading,
  onRefresh,
}: {
  installationId: string;
  acs: ListACsResult | null;
  loading: boolean;
  onRefresh: () => void;
}) {
  return (
    <div style={sectionStyle}>
      <div style={sectionHeaderStyle}>
        <h2 style={{ margin: 0 }}>Installation</h2>
        <button onClick={onRefresh} disabled={loading} style={smallButtonStyle}>
          {loading ? "loading…" : "refresh"}
        </button>
      </div>
      <div style={{ marginTop: "0.5rem", color: "#666" }}>
        <strong>id:</strong> <code>{installationId}</code>
      </div>
      <h3 style={{ margin: "1rem 0 0.5rem 0" }}>
        Registered ACs ({acs?.ac_names.length ?? 0})
      </h3>
      {acs === null && loading && (
        <p style={{ color: "#888" }}>Loading…</p>
      )}
      {acs !== null && acs.ac_names.length === 0 && (
        <p style={{ color: "#888" }}>
          No ACs registered yet. Use the Register panel below.
        </p>
      )}
      {acs !== null && acs.ac_names.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>name</th>
              <th style={thStyle}>workspace path</th>
            </tr>
          </thead>
          <tbody>
            {acs.ac_names.map((name, i) => (
              <tr key={name}>
                <td style={tdStyle}>
                  <code>{name}</code>
                </td>
                <td style={tdStyle}>
                  <code style={{ color: "#666", fontSize: "0.85rem" }}>
                    {acs.workspace_paths[i] ?? "—"}
                  </code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── RegisterPanel ─────────────────────────────────────────────────────

function RegisterPanel({
  disabled,
  sessionCommitted,
  onRegister,
  lastResult,
}: {
  disabled: boolean;
  sessionCommitted: boolean;
  onRegister: (workspaceDirName: string, description: string) => void;
  lastResult: RegisterACResult | null;
}) {
  const [dirName, setDirName] = useState("");
  const [description, setDescription] = useState("");

  return (
    <div style={sectionStyle}>
      <h2 style={{ margin: "0 0 0.25rem 0" }}>Register AC</h2>
      <p style={hintStyle}>
        Promote the active session's draft AC into the installation. The
        session must be in <code>COMMITTED</code> scope phase (commit it on
        the Workbench → Declare tab first).
      </p>
      {!sessionCommitted ? (
        <p style={{ color: "#a04020", fontSize: "0.9rem" }}>
          Current session is <strong>SELECTING</strong>. Commit scope to enable
          registration.
        </p>
      ) : (
        <>
          <div style={formRowStyle}>
            <label style={labelStyle}>
              workspace dir name (optional)
              <input
                value={dirName}
                onChange={(e) => setDirName(e.target.value)}
                placeholder="defaults to '<ac_name>.coframe'"
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              description (optional)
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={inputStyle}
              />
            </label>
            <button
              onClick={() => onRegister(dirName, description)}
              disabled={disabled}
              style={primaryButtonStyle}
            >
              Register
            </button>
          </div>
        </>
      )}
      {lastResult && (
        <div style={successBlockStyle}>
          ✓ Registered <code>{lastResult.ac_name}</code> — installation now has{" "}
          {lastResult.ac_count_after} AC(s). Workspace:{" "}
          <code style={{ fontSize: "0.85rem" }}>{lastResult.workspace_path}</code>
        </div>
      )}
    </div>
  );
}

// ── ForkPanel ─────────────────────────────────────────────────────────

function ForkPanel({
  disabled,
  knownACs,
  onFork,
  lastResult,
}: {
  disabled: boolean;
  knownACs: string[];
  onFork: (acName: string, newWorkspacePath: string) => void;
  lastResult: ForkSessionResult | null;
}) {
  const [acName, setAcName] = useState("");
  const [newPath, setNewPath] = useState("");
  return (
    <div style={sectionStyle}>
      <h2 style={{ margin: "0 0 0.25rem 0" }}>Fork Session</h2>
      <p style={hintStyle}>
        Per v2.1 §5.2: scope can't return to SELECTING in place. To revise a
        registered AC's structure, fork it into a fresh session — the new
        session starts with the AC's catalog pre-loaded and scope =
        SELECTING.
      </p>
      <div style={formRowStyle}>
        <label style={labelStyle}>
          AC to fork
          <select
            value={acName}
            onChange={(e) => setAcName(e.target.value)}
            style={selectStyle}
          >
            <option value="">— pick an AC —</option>
            {knownACs.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label style={labelStyle}>
          new workspace path
          <input
            value={newPath}
            onChange={(e) => setNewPath(e.target.value)}
            placeholder="./retail_revised.coframe"
            style={inputStyle}
          />
        </label>
        <button
          onClick={() => {
            if (acName.trim() === "" || newPath.trim() === "") return;
            onFork(acName, newPath);
          }}
          disabled={disabled || acName === "" || newPath.trim() === ""}
          style={buttonStyle}
        >
          Fork
        </button>
      </div>
      {lastResult && (
        <div style={successBlockStyle}>
          ✓ Forked <code>{lastResult.forked_from_ac}</code>. New session{" "}
          <code style={{ fontSize: "0.85rem" }}>
            {lastResult.new_session_id.slice(0, 8)}…
          </code>{" "}
          at <code style={{ fontSize: "0.85rem" }}>{lastResult.new_workspace_path}</code>{" "}
          (scope: <strong>{lastResult.scope_phase}</strong>).
          <div style={{ marginTop: "0.4rem", fontSize: "0.8rem", color: "#666" }}>
            Note: the new session isn't auto-activated — re-open it from the
            Workbench → Session tab with its workspace path.
          </div>
        </div>
      )}
    </div>
  );
}

function formatError(e: unknown): string {
  if (e instanceof ApiError) return `${e.status} ${e.message}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

// ── Styles ────────────────────────────────────────────────────────────

const subtitleStyle: React.CSSProperties = { color: "#888", margin: "0 0 1rem 0" };
const errorBlockStyle: React.CSSProperties = {
  padding: "0.6rem 0.8rem",
  background: "#ffeef0",
  border: "1px solid #f3c0c0",
  borderRadius: 4,
  marginTop: "1rem",
  marginBottom: "1rem",
  fontSize: "0.9rem",
};
const sectionStyle: React.CSSProperties = {
  padding: "1rem 1.25rem",
  background: "#fff",
  border: "1px solid #ddd",
  borderRadius: 4,
  marginBottom: "1rem",
};
const sectionHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.5rem",
};
const formRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "1rem",
  alignItems: "flex-end",
  flexWrap: "wrap",
  marginTop: "0.5rem",
};
const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  fontSize: "0.85rem",
  color: "#444",
};
const inputStyle: React.CSSProperties = {
  padding: "0.4rem 0.6rem",
  fontSize: "0.95rem",
  border: "1px solid #ccc",
  borderRadius: 4,
  minWidth: "16rem",
};
const selectStyle: React.CSSProperties = { ...inputStyle, minWidth: "12rem" };
const buttonStyle: React.CSSProperties = {
  padding: "0.5rem 1rem",
  background: "#0366d6",
  color: "white",
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: "0.95rem",
};
const primaryButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  background: "#1a6b3a",
};
const smallButtonStyle: React.CSSProperties = {
  padding: "0.25rem 0.6rem",
  background: "transparent",
  color: "#0366d6",
  border: "1px solid #cdddf2",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: "0.8rem",
};
const hintStyle: React.CSSProperties = {
  margin: "0 0 0.5rem 0",
  fontSize: "0.85rem",
  color: "#666",
};
const successBlockStyle: React.CSSProperties = {
  padding: "0.6rem 0.8rem",
  background: "#e6ffed",
  border: "1px solid #a5d8b8",
  borderRadius: 4,
  marginTop: "0.75rem",
  fontSize: "0.9rem",
  color: "#1a6b3a",
};
const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.9rem",
};
const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "0.4rem 0.6rem",
  background: "#fafafa",
  borderBottom: "2px solid #ddd",
  fontSize: "0.8rem",
  color: "#666",
  fontWeight: 500,
};
const tdStyle: React.CSSProperties = {
  padding: "0.35rem 0.6rem",
  borderBottom: "1px solid #eee",
  verticalAlign: "top",
};
